"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  LAUNCH_NEAR_ME_USER_DEFAULTS_POLICY,
  mergeNearMeUserDefaultsPolicyFromRow,
  type NearMeUserDefaultsPolicy,
} from "@/lib/notifications/near-me-user-defaults-policy";
import {
  DEFAULT_HOMEPAGE_DISCOVERY_GRANULARITY,
  parseHomepageDiscoveryGranularity,
  type HomepageDiscoveryGranularity,
} from "@/lib/items/homepageDiscoveryGranularity";

interface SystemSettingsContextType {
  googleMapsEnabled: boolean;
  recentItemRequestsEnabled: boolean;
  /** When false, near-me new listing notifications are off (matches system_settings). */
  newListingNearMeNotificationsEnabled: boolean;
  /** Admin-configured defaults for new near-me subscribers (onboarding / signup). */
  nearMeUserDefaultsPolicy: NearMeUserDefaultsPolicy;
  /** When false, items in the "other" location tier are hidden (Home + Market). */
  includeOtherLocationTier: boolean;
  /** Buyer "Confirmed available" badge on cards and item detail. */
  buyerAvailabilityConfirmedEnabled: boolean;
  /** "Still available near you" browse shelf on home and market. */
  buyerAvailabilityConfirmedShelfEnabled: boolean;
  /** Days after seller confirmation that the buyer badge stays visible. */
  buyerAvailabilityConfirmedWindowDays: number;
  /** When true, confirmed listings get a small in-tier sort boost on Market (newest). */
  buyerAvailabilityConfirmedSortBoostEnabled: boolean;
  /** Max location reach for homepage + market discovery rows. */
  homepageDiscoveryGranularity: HomepageDiscoveryGranularity;
  /** Widen discovery one tier when shelves are thin. */
  homepageDiscoveryAutoWidenEnabled: boolean;
  /** When false, hide listing views + near-me reach on seller My Items (Phase 5b). */
  sellerMyItemsVisibilityMetricsEnabled: boolean;
  isLoading: boolean;
  refreshSettings: () => Promise<void>;
}

const SystemSettingsContext = createContext<
  SystemSettingsContextType | undefined
>(undefined);

interface SystemSettingsProviderProps {
  children: ReactNode;
}

// Cache for system settings with TTL (Time To Live)
interface SettingsCache {
  data: {
    google_maps_enabled: boolean;
    recent_item_requests_enabled: boolean;
    new_listing_near_me_notifications_enabled: boolean;
    include_other_location_tier: boolean;
    buyer_availability_confirmed_enabled: boolean;
    buyer_availability_confirmed_shelf_enabled: boolean;
    buyer_availability_confirmed_window_days: number;
    buyer_availability_confirmed_sort_boost_enabled: boolean;
    homepage_discovery_granularity: HomepageDiscoveryGranularity;
    homepage_discovery_auto_widen_enabled: boolean;
    seller_my_items_visibility_metrics_enabled: boolean;
    near_me_user_defaults_policy?: NearMeUserDefaultsPolicy;
  };
  timestamp: number;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
let settingsCache: SettingsCache | null = null;

export function SystemSettingsProvider({
  children,
}: SystemSettingsProviderProps) {
  const [googleMapsEnabled, setGoogleMapsEnabled] = useState(true); // Default to true
  const [recentItemRequestsEnabled, setRecentItemRequestsEnabled] =
    useState(false); // Default to off
  const [includeOtherLocationTier, setIncludeOtherLocationTier] =
    useState(true);
  const [
    buyerAvailabilityConfirmedEnabled,
    setBuyerAvailabilityConfirmedEnabled,
  ] = useState(true);
  const [
    buyerAvailabilityConfirmedShelfEnabled,
    setBuyerAvailabilityConfirmedShelfEnabled,
  ] = useState(true);
  const [
    buyerAvailabilityConfirmedWindowDays,
    setBuyerAvailabilityConfirmedWindowDays,
  ] = useState(7);
  const [
    buyerAvailabilityConfirmedSortBoostEnabled,
    setBuyerAvailabilityConfirmedSortBoostEnabled,
  ] = useState(false);
  const [homepageDiscoveryGranularity, setHomepageDiscoveryGranularity] =
    useState<HomepageDiscoveryGranularity>(
      DEFAULT_HOMEPAGE_DISCOVERY_GRANULARITY,
    );
  const [
    homepageDiscoveryAutoWidenEnabled,
    setHomepageDiscoveryAutoWidenEnabled,
  ] = useState(true);
  const [
    sellerMyItemsVisibilityMetricsEnabled,
    setSellerMyItemsVisibilityMetricsEnabled,
  ] = useState(true);
  const [
    newListingNearMeNotificationsEnabled,
    setNewListingNearMeNotificationsEnabled,
  ] = useState(true);
  const [nearMeUserDefaultsPolicy, setNearMeUserDefaultsPolicy] =
    useState<NearMeUserDefaultsPolicy>(LAUNCH_NEAR_ME_USER_DEFAULTS_POLICY);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSettings = async () => {
    try {
      // Check cache first
      const now = Date.now();
      if (settingsCache && now - settingsCache.timestamp < CACHE_DURATION) {
        setGoogleMapsEnabled(settingsCache.data.google_maps_enabled);
        setRecentItemRequestsEnabled(
          settingsCache.data.recent_item_requests_enabled,
        );
        setIncludeOtherLocationTier(
          settingsCache.data.include_other_location_tier,
        );
        setBuyerAvailabilityConfirmedEnabled(
          settingsCache.data.buyer_availability_confirmed_enabled,
        );
        setBuyerAvailabilityConfirmedShelfEnabled(
          settingsCache.data.buyer_availability_confirmed_shelf_enabled,
        );
        setBuyerAvailabilityConfirmedWindowDays(
          settingsCache.data.buyer_availability_confirmed_window_days,
        );
        setBuyerAvailabilityConfirmedSortBoostEnabled(
          settingsCache.data.buyer_availability_confirmed_sort_boost_enabled ===
            true,
        );
        setHomepageDiscoveryGranularity(
          settingsCache.data.homepage_discovery_granularity,
        );
        setHomepageDiscoveryAutoWidenEnabled(
          settingsCache.data.homepage_discovery_auto_widen_enabled,
        );
        setSellerMyItemsVisibilityMetricsEnabled(
          settingsCache.data.seller_my_items_visibility_metrics_enabled !==
            false,
        );
        setNewListingNearMeNotificationsEnabled(
          settingsCache.data.new_listing_near_me_notifications_enabled ?? true,
        );
        setNearMeUserDefaultsPolicy(
          settingsCache.data.near_me_user_defaults_policy ??
            LAUNCH_NEAR_ME_USER_DEFAULTS_POLICY,
        );
        setIsLoading(false);
        console.log("[SystemSettings] Loaded from cache:", {
          googleMapsEnabled: settingsCache.data.google_maps_enabled,
          recentItemRequestsEnabled:
            settingsCache.data.recent_item_requests_enabled,
          newListingNearMeNotificationsEnabled:
            settingsCache.data.new_listing_near_me_notifications_enabled,
          includeOtherLocationTier:
            settingsCache.data.include_other_location_tier,
        });
        return;
      }

      const response = await fetch("/api/settings/system", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        // Use cache with revalidation for better performance
        cache: "force-cache",
        next: { revalidate: 300 }, // Revalidate every 5 minutes
      });

      if (response.ok) {
        const data = await response.json();
        setGoogleMapsEnabled(data.google_maps_enabled);
        setRecentItemRequestsEnabled(
          data.recent_item_requests_enabled ??
            data.settings?.recent_item_requests_enabled ??
            false,
        );
        const includeTier =
          data.include_other_location_tier ??
          data.settings?.include_other_location_tier ??
          true;
        setIncludeOtherLocationTier(includeTier);
        const buyerBadgeEnabled =
          data.buyer_availability_confirmed_enabled ??
          data.settings?.buyer_availability_confirmed_enabled ??
          true;
        setBuyerAvailabilityConfirmedEnabled(buyerBadgeEnabled !== false);
        const buyerShelfEnabled =
          data.buyer_availability_confirmed_shelf_enabled ??
          data.settings?.buyer_availability_confirmed_shelf_enabled ??
          true;
        setBuyerAvailabilityConfirmedShelfEnabled(buyerShelfEnabled !== false);
        const buyerBadgeWindowDays =
          data.buyer_availability_confirmed_window_days ??
          data.settings?.buyer_availability_confirmed_window_days ??
          7;
        setBuyerAvailabilityConfirmedWindowDays(
          buyerBadgeWindowDays > 0 ? buyerBadgeWindowDays : 7,
        );
        const buyerSortBoostEnabled =
          data.buyer_availability_confirmed_sort_boost_enabled ??
          data.settings?.buyer_availability_confirmed_sort_boost_enabled ??
          false;
        setBuyerAvailabilityConfirmedSortBoostEnabled(
          buyerSortBoostEnabled === true,
        );
        const discoveryGranularity = parseHomepageDiscoveryGranularity(
          data.homepage_discovery_granularity ??
            data.settings?.homepage_discovery_granularity,
        );
        setHomepageDiscoveryGranularity(discoveryGranularity);
        const discoveryAutoWiden =
          data.homepage_discovery_auto_widen_enabled ??
          data.settings?.homepage_discovery_auto_widen_enabled ??
          true;
        setHomepageDiscoveryAutoWidenEnabled(discoveryAutoWiden !== false);
        const sellerMyItemsMetricsEnabled =
          data.seller_my_items_visibility_metrics_enabled ??
          data.settings?.seller_my_items_visibility_metrics_enabled ??
          true;
        setSellerMyItemsVisibilityMetricsEnabled(
          sellerMyItemsMetricsEnabled !== false,
        );
        const nearMeEnabled =
          data.new_listing_near_me_notifications_enabled ??
          data.settings?.new_listing_near_me_notifications_enabled ??
          true;
        setNewListingNearMeNotificationsEnabled(nearMeEnabled);
        const nearMePolicy = mergeNearMeUserDefaultsPolicyFromRow({
          near_me_default_granularity:
            data.near_me_user_defaults_policy?.near_me_default_granularity ??
            data.settings?.near_me_user_defaults_policy
              ?.near_me_default_granularity,
          near_me_default_delivery_mode_state:
            data.near_me_user_defaults_policy
              ?.near_me_default_delivery_mode_state ??
            data.settings?.near_me_user_defaults_policy
              ?.near_me_default_delivery_mode_state,
          near_me_default_delivery_mode_lga:
            data.near_me_user_defaults_policy
              ?.near_me_default_delivery_mode_lga ??
            data.settings?.near_me_user_defaults_policy
              ?.near_me_default_delivery_mode_lga,
          near_me_default_delivery_mode_neighborhood:
            data.near_me_user_defaults_policy
              ?.near_me_default_delivery_mode_neighborhood ??
            data.settings?.near_me_user_defaults_policy
              ?.near_me_default_delivery_mode_neighborhood,
        });
        setNearMeUserDefaultsPolicy(nearMePolicy);

        // Update cache
        settingsCache = {
          data: {
            google_maps_enabled: data.google_maps_enabled,
            recent_item_requests_enabled:
              data.recent_item_requests_enabled ??
              data.settings?.recent_item_requests_enabled ??
              false,
            new_listing_near_me_notifications_enabled: nearMeEnabled,
            include_other_location_tier: includeTier,
            buyer_availability_confirmed_enabled: buyerBadgeEnabled !== false,
            buyer_availability_confirmed_shelf_enabled:
              buyerShelfEnabled !== false,
            buyer_availability_confirmed_window_days:
              buyerBadgeWindowDays > 0 ? buyerBadgeWindowDays : 7,
            buyer_availability_confirmed_sort_boost_enabled:
              buyerSortBoostEnabled === true,
            homepage_discovery_granularity: discoveryGranularity,
            homepage_discovery_auto_widen_enabled: discoveryAutoWiden !== false,
            seller_my_items_visibility_metrics_enabled:
              sellerMyItemsMetricsEnabled !== false,
            near_me_user_defaults_policy: nearMePolicy,
          },
          timestamp: now,
        };

        console.log("[SystemSettings] Loaded:", {
          googleMapsEnabled: data.google_maps_enabled,
          recentItemRequestsEnabled:
            data.recent_item_requests_enabled ??
            data.settings?.recent_item_requests_enabled ??
            false,
          newListingNearMeNotificationsEnabled: nearMeEnabled,
          includeOtherLocationTier: includeTier,
        });
      } else {
        console.error("[SystemSettings] API error:", response.status);
        // Keep current state on error
      }
    } catch (error) {
      console.error("[SystemSettings] Fetch error:", error);
      // Keep current state on error
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSettings = async () => {
    setIsLoading(true);
    // Force refresh so admin toggles reflect immediately.
    settingsCache = null;
    await fetchSettings();
  };

  useEffect(() => {
    // Defer settings fetch to avoid blocking initial render
    // Use requestIdleCallback if available, otherwise setTimeout
    let timeoutId: ReturnType<typeof setTimeout> | number | null = null;

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      timeoutId = requestIdleCallback(() => fetchSettings(), { timeout: 2000 });
    } else {
      timeoutId = setTimeout(() => fetchSettings(), 100);
    }

    return () => {
      if (timeoutId !== null) {
        if (typeof timeoutId === "number") {
          // requestIdleCallback returns a number ID, cancelIdleCallback takes a number
          if (typeof window !== "undefined" && "cancelIdleCallback" in window) {
            cancelIdleCallback(timeoutId);
          } else {
            clearTimeout(timeoutId);
          }
        } else {
          clearTimeout(timeoutId);
        }
      }
    };
  }, []);

  const contextValue: SystemSettingsContextType = {
    googleMapsEnabled,
    recentItemRequestsEnabled,
    newListingNearMeNotificationsEnabled,
    nearMeUserDefaultsPolicy,
    buyerAvailabilityConfirmedEnabled,
    buyerAvailabilityConfirmedShelfEnabled,
    buyerAvailabilityConfirmedWindowDays,
    buyerAvailabilityConfirmedSortBoostEnabled,
    homepageDiscoveryGranularity,
    homepageDiscoveryAutoWidenEnabled,
    sellerMyItemsVisibilityMetricsEnabled,
    includeOtherLocationTier,
    isLoading,
    refreshSettings,
  };

  return (
    <SystemSettingsContext.Provider value={contextValue}>
      {children}
    </SystemSettingsContext.Provider>
  );
}

export function useSystemSettings(): SystemSettingsContextType {
  const context = useContext(SystemSettingsContext);
  if (context === undefined) {
    throw new Error(
      "useSystemSettings must be used within a SystemSettingsProvider",
    );
  }
  return context;
}
